import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, Link } from 'react-router-dom';
import { RefreshCw, Search, Timer, X } from 'lucide-react';

import { Breadcrumb } from '../components/Breadcrumb';
import { DataTable, ColumnDef } from '../components/DataTable';
import { QuoteSummaryCards } from '../components/quotes/QuoteSummaryCards';
import { apiService } from '../services/apiService';
import {
  OpenQuote,
  QuoteDashboardSummary,
  AgentStatus,
} from '../services/types';
import { parseCalendarDate } from '../utils/calendarDate';

const STATUS_BADGES: Record<string, { label: string; className: string }> = {
  drafted: {
    label: 'quoteDashboard.status.drafted',
    className: 'bg-green-100 text-green-800',
  },
  skipped_by_claude: {
    label: 'quoteDashboard.status.skipped',
    className: 'bg-yellow-100 text-yellow-800',
  },
  no_thread_found: {
    label: 'quoteDashboard.status.noThread',
    className: 'bg-red-100 text-red-800',
  },
};

const DEFAULT_BADGE = {
  label: 'quoteDashboard.status.notProcessed',
  className: 'bg-gray-100 text-gray-600',
};

const NO_RUN_BADGE = {
  label: 'quoteDashboard.status.noRun',
  className: 'bg-gray-100 text-gray-400',
};

const AUTO_REFRESH_MS = 5 * 60 * 1000; // 5 minutes

function formatDate(isoDate: string): string {
  if (!isoDate) return '—';
  try {
    return parseCalendarDate(isoDate).toLocaleDateString();
  } catch {
    return isoDate;
  }
}

function computeAgeDays(dateQuoted: string): number | null {
  if (!dateQuoted) return null;
  try {
    const quoted = new Date(dateQuoted);
    const now = new Date();
    return Math.floor(
      (now.getTime() - quoted.getTime()) / (1000 * 60 * 60 * 24)
    );
  } catch {
    return null;
  }
}

function formatCurrency(value: number): string {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function getStatusBadge(status: AgentStatus | null) {
  const badge = status ? STATUS_BADGES[status] || DEFAULT_BADGE : DEFAULT_BADGE;
  return badge;
}

// Side panel for showing quote info + agent status
const QuoteSidePanel: React.FC<{
  quote: OpenQuote;
  onClose: () => void;
}> = ({ quote, onClose }) => {
  const { t } = useTranslation();
  const badge = !quote.followUpDate
    ? NO_RUN_BADGE
    : getStatusBadge(quote.agentStatus);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-end">
      <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('quoteDashboard.detail.title')} #{quote.quoteNum}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Quote Info */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
              {t('quoteDashboard.panel.quoteInfo')}
            </h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">
                  {t('quoteDashboard.columns.customer')}
                </p>
                <p className="font-medium">{quote.customerName || '—'}</p>
              </div>
              {quote.shipToName && (
                <div>
                  <p className="text-sm text-gray-500">
                    {t('quoteDashboard.columns.shipTo')}
                  </p>
                  <p className="font-medium">{quote.shipToName}</p>
                </div>
              )}
              {quote.agentQuoteDescription && (
                <div>
                  <p className="text-sm text-gray-500">
                    {t('quoteDashboard.columns.description')}
                  </p>
                  <p className="font-medium">{quote.agentQuoteDescription}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">
                    {t('quoteDashboard.columns.dateQuoted')}
                  </p>
                  <p className="font-medium">{formatDate(quote.dateQuoted)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                    {t('quoteDashboard.columns.followUpDate')}
                  </p>
                  <p className="font-medium">
                    {formatDate(quote.followUpDate)}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">
                    {t('quoteDashboard.columns.totalValue')}
                  </p>
                  <p className="font-medium">
                    ${formatCurrency(quote.docTotalQuote)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                    {t('quoteDashboard.columns.age')}
                  </p>
                  <p className="font-medium">
                    {computeAgeDays(quote.dateQuoted) ?? '—'}
                    {computeAgeDays(quote.dateQuoted) !== null ? 'd' : ''}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Agent Info */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
              {t('quoteDashboard.panel.agentInfo')}
            </h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">
                  {t('quoteDashboard.columns.lastStatus')}
                </p>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.className}`}
                >
                  {t(badge.label)}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-500">
                  {t('quoteDashboard.columns.lastRun')}
                </p>
                <p className="font-medium">{quote.agentRunDate || '—'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">
                  {t('quoteDashboard.columns.reason')}
                </p>
                <p className="text-sm mt-1 bg-gray-50 p-3 rounded-lg">
                  {quote.agentReason || '—'}
                </p>
              </div>
            </div>
          </div>

          {/* Link to full tracker */}
          <Link
            to={`/quotes/${quote.quoteNum}`}
            className="block w-full text-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition"
          >
            {t('quoteDashboard.panel.viewFullDetails')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export const QuoteDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [quotes, setQuotes] = useState<OpenQuote[]>([]);
  const [summary, setSummary] = useState<QuoteDashboardSummary>({
    totalQuotes: 0,
    drafted: 0,
    skipped: 0,
    noThreadFound: 0,
    notProcessed: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState<OpenQuote | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Filter & sort state from URL params
  const filterText = searchParams.get('filter') || '';
  const sortColumn = searchParams.get('sort') || 'quoteNum';
  const sortDir = searchParams.get('dir') || 'desc';

  const fetchQuotes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getOpenQuotes();
      setQuotes(data.quotes);
      setSummary(data.summary);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load quotes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchQuotes, AUTO_REFRESH_MS);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, fetchQuotes]);

  const updateFilter = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('filter', value);
    } else {
      params.delete('filter');
    }
    setSearchParams(params, { replace: true });
  };

  const handleSort = (columnKey: string) => {
    const params = new URLSearchParams(searchParams);
    if (sortColumn === columnKey) {
      params.set('dir', sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      params.set('sort', columnKey);
      params.set('dir', 'asc');
    }
    setSearchParams(params, { replace: true });
  };

  // Sort indicator arrows like Fulfillment page
  const getSortIndicator = (column: string) => {
    if (sortColumn !== column) return '';
    return sortDir === 'asc' ? ' \u2191' : ' \u2193';
  };

  // Filter quotes
  const filteredQuotes = quotes.filter((q) => {
    if (!filterText) return true;
    const search = filterText.toLowerCase();
    return (
      String(q.quoteNum).includes(search) ||
      q.customerName.toLowerCase().includes(search) ||
      q.shipToName.toLowerCase().includes(search) ||
      q.custId.toLowerCase().includes(search) ||
      (q.agentQuoteDescription || '').toLowerCase().includes(search)
    );
  });

  // Sort quotes
  const sortedQuotes = [...filteredQuotes].sort((a, b) => {
    // Special handling for computed 'age' column — sort by dateQuoted inversely
    if (sortColumn === 'age') {
      const ageA = computeAgeDays(a.dateQuoted);
      const ageB = computeAgeDays(b.dateQuoted);
      if (ageA === null) return 1;
      if (ageB === null) return -1;
      const comparison = ageA - ageB;
      return sortDir === 'desc' ? -comparison : comparison;
    }

    const key = sortColumn as keyof OpenQuote;
    const aVal = a[key];
    const bVal = b[key];

    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;

    let comparison = 0;
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      comparison = aVal - bVal;
    } else {
      comparison = String(aVal).localeCompare(String(bVal));
    }

    return sortDir === 'desc' ? -comparison : comparison;
  });

  const columns: ColumnDef<OpenQuote>[] = [
    {
      key: 'quoteNum',
      header: `${t('quoteDashboard.columns.quoteNum')}${getSortIndicator('quoteNum')}`,
      sortable: true,
      render: (value) => (
        <Link
          to={`/quotes/${value as number}`}
          className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {value as number}
        </Link>
      ),
    },
    {
      key: 'customerName',
      header: `${t('quoteDashboard.columns.customer')}${getSortIndicator('customerName')}`,
      sortable: true,
      render: (_value, row) => {
        const q = row as OpenQuote;
        if (q.shipToName && q.shipToName !== q.customerName) {
          return (
            <span>
              {q.customerName}
              <span className="text-gray-400 mx-1">&gt;</span>
              <span className="text-gray-600">{q.shipToName}</span>
            </span>
          );
        }
        return q.customerName || '—';
      },
    },
    {
      key: 'agentQuoteDescription',
      header: `${t('quoteDashboard.columns.description')}${getSortIndicator('agentQuoteDescription')}`,
      sortable: true,
      render: (value) => {
        const desc = value as string | null;
        if (!desc) return '—';
        return (
          <span
            className="text-sm text-gray-700 max-w-[250px] truncate block"
            title={desc}
          >
            {desc}
          </span>
        );
      },
    },
    {
      key: 'dateQuoted',
      header: `${t('quoteDashboard.columns.dateQuoted')}${getSortIndicator('dateQuoted')}`,
      sortable: true,
      render: (value) => formatDate(value as string),
    },
    {
      key: 'age',
      header: `${t('quoteDashboard.columns.age')}${getSortIndicator('age')}`,
      sortable: true,
      align: 'right',
      render: (_value, row) => {
        const days = computeAgeDays((row as OpenQuote).dateQuoted);
        if (days === null) return '—';
        return `${days}d`;
      },
    },
    {
      key: 'docTotalQuote',
      header: `${t('quoteDashboard.columns.totalValue')}${getSortIndicator('docTotalQuote')}`,
      sortable: true,
      align: 'right',
      render: (value) => `$${formatCurrency(value as number)}`,
    },
    {
      key: 'followUpDate',
      header: `${t('quoteDashboard.columns.followUpDate')}${getSortIndicator('followUpDate')}`,
      sortable: true,
      render: (value) => formatDate(value as string),
    },
    {
      key: 'agentStatus',
      header: `${t('quoteDashboard.columns.lastStatus')}${getSortIndicator('agentStatus')}`,
      sortable: true,
      render: (value, row) => {
        const quote = row as OpenQuote;
        const badge = !quote.followUpDate
          ? NO_RUN_BADGE
          : getStatusBadge(value as AgentStatus | null);
        return (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.className}`}
          >
            {t(badge.label)}
          </span>
        );
      },
    },
    {
      key: 'agentRunDate',
      header: `${t('quoteDashboard.columns.lastRun')}${getSortIndicator('agentRunDate')}`,
      sortable: true,
      render: (value) => (value as string) || '—',
    },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 w-full">
      <Breadcrumb currentPage={t('navigation.quoteDashboard')} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('quoteDashboard.title')}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('quoteDashboard.subtitle', {
              count: summary.totalQuotes,
            })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Auto-refresh toggle */}
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition ${
              autoRefresh
                ? 'bg-blue-50 border-blue-300 text-blue-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Timer className="w-4 h-4" />
            {t('quoteDashboard.autoRefresh')}
          </button>

          {/* Manual refresh */}
          <button
            onClick={fetchQuotes}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {t('quoteDashboard.refresh')}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
          <p className="font-semibold">{t('common.error')}</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Summary Cards */}
      <QuoteSummaryCards summary={summary} />

      {/* Search/Filter */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={filterText}
            onChange={(e) => updateFilter(e.target.value)}
            placeholder={t('quoteDashboard.filterPlaceholder')}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        {filterText && (
          <p className="text-sm text-gray-500 mt-2">
            {t('quoteDashboard.showingFiltered', {
              filtered: filteredQuotes.length,
              total: quotes.length,
            })}
          </p>
        )}
      </div>

      {/* Data Table */}
      {loading && quotes.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">{t('common.loading')}</span>
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={sortedQuotes}
          keyExtractor={(row) => row.quoteNum}
          onRowClick={(row) => setSelectedQuote(row)}
          onColumnSort={handleSort}
          emptyMessage={t('quoteDashboard.noQuotes')}
        />
      )}

      {/* Side Panel */}
      {selectedQuote && (
        <QuoteSidePanel
          quote={selectedQuote}
          onClose={() => setSelectedQuote(null)}
        />
      )}
    </div>
  );
};
